import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { AdminPanel } from '../../../core/interfaces';
import { TSP } from '../../../core/models';
import { PortalService } from '../../../core/services/portal/portal.service';
import { ConfirmationDialog } from '../../../shared/components/confirmation-dialog/confirmation-dialog';
import { EntityListComponent } from '../../../shared/components/entity-list/entity-list.component';
import { CreatePortalForm } from '../create-portal-form/create-portal-form';
import { UpdatePortalForm } from '../update-portal-form/update-portal-form';

@Component({
  selector: 'app-portals',
  imports: [CommonModule, EntityListComponent],
  template: `
    <app-entity-list
      title="Портали"
      [columns]="columns"
      [data]="portals$ | async"
      [totalItems]="totalItems$ | async"
      [pageSize]="pageSize$ | async"
      [pageIndex]="pageIndex$ | async"
      [loading]="loading"
      (pageChange)="onPageChange($event)"
      (sortChange)="onSortChange($event)"
      (searchChange)="onSearch($event)"
      (action)="onAction($event)"
    >
    </app-entity-list>
  `,
})
export class PortalsComponent implements OnInit, OnDestroy {
  private readonly portalService = inject(PortalService);
  private readonly dialog = inject(MatDialog);

  public columns: AdminPanel.Entity.EntityColumn[] = [
    { key: 'id', label: 'ID', sortable: true, sortKey: 'id', type: 'number' },
    { key: 'name', label: 'Назва', sortable: true, sortKey: 'name', type: 'text' },
    { key: 'jobBoardUrl', label: 'Публічна адреса', sortable: false, type: 'text' },
    {
      key: 'subscription.plan',
      label: 'План',
      sortable: false,
      type: 'enum',
      options: {
        trial: 'Пробний',
        professional: 'Професійний',
        team: 'Командний',
        enterprise: 'Корпоративний',
        free: 'Безкоштовний',
      },
    },
    { key: 'subscription.endDate', label: 'Кінцева дата', sortable: false, type: 'date' },
  ];

  public portals$: Observable<TSP.Internal.Recruiter.Portal.Details[]> = of([]);
  public totalItems$: Observable<number> = of(0);
  public loading = false;
  public pageSize$: Observable<number> = of(10);
  public pageIndex$: Observable<number> = of(0);

  #sortBy: BehaviorSubject<string> = new BehaviorSubject('createdAt');
  #sortOrder: BehaviorSubject<'asc' | 'desc'> = new BehaviorSubject('desc');
  #search: BehaviorSubject<string> = new BehaviorSubject('');
  #pageSize: BehaviorSubject<number> = new BehaviorSubject(10);
  #pageIndex: BehaviorSubject<number> = new BehaviorSubject(0);
  #dateTime: BehaviorSubject<number> = new BehaviorSubject(0);
  #ngUnsubscribe: Subject<void> = new Subject<void>();

  public ngOnInit(): void {
    this.loadData();
  }

  public ngOnDestroy(): void {
    this.#ngUnsubscribe.next();
    this.#ngUnsubscribe.complete();
  }

  public loadData(): void {
    const sort$ = this.#sortBy.asObservable().pipe(distinctUntilChanged());
    const sortOrder$ = this.#sortOrder.asObservable().pipe(distinctUntilChanged());
    const search$ = this.#search.asObservable().pipe(distinctUntilChanged());
    const dateTime$ = this.#dateTime.asObservable().pipe(distinctUntilChanged());

    this.pageIndex$ = this.#pageIndex.asObservable().pipe(distinctUntilChanged());
    this.pageSize$ = this.#pageSize.asObservable().pipe(distinctUntilChanged());

    const data$ = combineLatest([this.pageIndex$, this.pageSize$, sort$, dateTime$, sortOrder$, search$]).pipe(
      tap(() => (this.loading = true)),
      switchMap(([pageIndex, pageSize, sortBy, , sortOrder, search]) =>
        this.portalService.getPortals({ page: pageIndex, size: pageSize, sortBy, sortOrder, search }),
      ),
      tap(() => (this.loading = false)),
      catchError((error) => {
        this.loading = false;
        return throwError(() => error);
      }),
      shareReplay(),
      takeUntil(this.#ngUnsubscribe),
    );

    this.portals$ = data$.pipe(map((data) => data.items));
    this.totalItems$ = data$.pipe(map((data) => data.totalCount));
  }

  public onPageChange(event: PageEvent): void {
    this.#pageIndex.next(event.pageIndex);
    this.#pageSize.next(event.pageSize);
  }

  public onSortChange(sort: Sort): void {
    this.#sortBy.next(sort.active);
    this.#sortOrder.next((sort.direction || 'desc') as 'asc' | 'desc');
  }

  public onSearch(searchTerm: string): void {
    this.#search.next(searchTerm);
  }

  public onAction(action: AdminPanel.Entity.EntityAction): void {
    switch (action.type) {
      case 'create':
        this.dialog
          .open(CreatePortalForm, {
            maxWidth: '920px',
            width: '800px',
          })
          .afterClosed()
          .pipe(
            takeUntil(this.#ngUnsubscribe),
            switchMap((result: TSP.Internal.Recruiter.Portal.CreatePayload | null) => {
              if (result) {
                return this.portalService.createPortal(result);
              }
              return of(null);
            }),
          )
          .subscribe((portal: TSP.Internal.Recruiter.Portal.Details | null) => {
            if (portal) {
              this.#dateTime.next(Date.now());
            }
          });
        break;
      case 'edit':
        if (action.item) {
          this.dialog
            .open(UpdatePortalForm, {
              maxWidth: '920px',
              width: '800px',
              data: action.item as TSP.Internal.Recruiter.Portal.Details,
            })
            .afterClosed()
            .pipe(
              takeUntil(this.#ngUnsubscribe),
              switchMap((result: TSP.Internal.Recruiter.Portal.UpdatePayload | null) => {
                if (result) {
                  return this.portalService.updatePortal(result);
                }
                return of(null);
              }),
            )
            .subscribe((portal: TSP.Internal.Recruiter.Portal.Details | null) => {
              if (portal) {
                this.#dateTime.next(Date.now());
              }
            });
        }
        break;
      case 'delete':
        if (action.item) {
          this.dialog
            .open(ConfirmationDialog, {
              data: { text: 'Чи дійсно хочете видалити цей портал?' },
            })
            .afterClosed()
            .pipe(takeUntil(this.#ngUnsubscribe))
            .subscribe((result: boolean) => {
              if (result) {
                this.portalService.deletePortal(action.item.id).subscribe(() => {
                  this.#dateTime.next(Date.now());
                });
              }
            });
        }
        break;
    }
  }
}
